import { FocusEvent, ReactNode, useCallback } from 'react';

import { useField } from 'react-final-form';

import { TreeViewAutocomplete as Input } from '@vk-hr-tek/ui/input';
import { TreeViewItem as Item } from '@vk-hr-tek/ui/TreeView';

import { compose, ValidateType } from '../validation';

/**
 * Узел дерева.
 *
 * @typeParam T - Тип данных узла.
 */
type TreeNode<T> = {
  /**
   * Дочерние узлы.
   */
  children?: Option<T>[];
};

/**
 * Фильтр для поиска.
 */
interface Filter {
  /**
   * Ограничение количества результатов.
   */
  limit: number;

  /**
   * Смещение результатов.
   */
  offset: number;

  /**
   * Поисковый запрос.
   */
  query: string;
}

/**
 * Опция дерева.
 *
 * @typeParam T - Тип данных опции.
 */
type Option<T> = Omit<T, 'children'> & TreeNode<T>;

/**
 * Свойства компонента {@link TreeViewAutocomplete}.
 *
 * @typeParam T - Тип данных опции.
 */
interface TreeViewAutocompleteProps<T> {
  /**
   * Флаг, указывающий, отключено ли поле.
   */
  disabled?: boolean;

  /**
   * Имя поля.
   */
  name: string;

  /**
   * Метка поля.
   */
  label?: string;

  /**
   * Иконка для метки.
   */
  labelIcon?: ReactNode;

  /**
   * Всплывающая подсказка.
   */
  tooltip?: ReactNode;

  /**
   * Флаг, указывающий, является ли поле обязательным.
   */
  required?: boolean;

  /**
   * Опции для автодополнения.
   */
  options: Option<T>[];

  /**
   * Текст, отображаемый, когда нет доступных опций.
   */
  noOptionsText?: string;

  /**
   * Текст-заполнитель поля.
   */
  placeholder?: string;

  /**
   * Флаг, указывающий, можно ли очистить поле.
   */
  clearable?: boolean;

  /**
   * Флаг, указывающий на состояние загрузки.
   */
  loading?: boolean;

  /**
   * Функция для получения метки опции.
   */
  getOptionLabel?: (option: T) => string;

  /**
   * Валидаторы поля.
   */
  validate?: ValidateType[] | ValidateType;

  /**
   * Коллбэк на изменение значения поля.
   */
  onChange?: (value?: string, item?: Item) => void;

  /**
   * Коллбэк на событие потери фокуса полем.
   */
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;

  /**
   * Коллбэк на загрузку дочерних узлов.
   */
  onChildrenLoad?: (value: string) => Promise<T[]>;

  /**
   * Коллбэк на поиск опций.
   */
  onSearch?: (filter: Filter) => Promise<T[]>;

  /**
   * Коллбэк на загрузку дерева опций.
   */
  onLoadTree?: (unitId: string) => Promise<Option<T>[]>;

  /**
   * Идентификатор для тестирования.
   */
  testId?: string;

  /**
   * Флаг для отключения внешних отступов.
   */
  withoutMargins?: boolean;

  /**
   * Кастомный метод рендера опции в списке поиска.
   */
  renderSearchOption?: (option: T, highlightedLabel: ReactNode) => ReactNode;

  /**
   * Кастомный метод рендера элементов в древовидном представлении.
   */
  renderOption?: (item: Item) => ReactNode;
}

/**
 * Компонент для автодополнения с поддержкой дерева опций.
 *
 * @public
 * @typeParam T - Тип данных опции.
 * @param disabled - Флаг, указывающий, отключено ли поле.
 * @param name - Имя поля.
 * @param label - Метка поля.
 * @param labelIcon - Иконка для метки.
 * @param tooltip - Всплывающая подсказка.
 * @param options - Опции для автодополнения.
 * @param placeholder - Текст-заполнитель поля.
 * @param clearable - Флаг, указывающий, можно ли очистить поле.
 * @param loading - Флаг, указывающий на состояние загрузки.
 * @param required - Флаг, указывающий, является ли поле обязательным.
 * @param getOptionLabel - Функция для получения метки опции.
 * @param noOptionsText - Текст, отображаемый, когда нет доступных опций.
 * @param validate - Валидаторы поля.
 * @param onChange - Коллбэк на изменение значения поля.
 * @param onChildrenLoad - Коллбэк на загрузку дочерних узлов.
 * @param onSearch - Коллбэк на поиск опций.
 * @param onLoadTree - Коллбэк на загрузку дерева опций.
 * @param testId - Идентификатор для тестирования.
 * @param renderSearchOption - Кастомный метод рендера опции в списке поиска.
 * @param renderOption - Кастомный метод рендера элементов в древовидном представлении.
 * @param withoutMargins - Флаг для отключения внешних отступов.
 * @returns Элемент React.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link TreeViewAutocompleteProps}
 */
export const TreeViewAutocomplete = <T extends { id: string; label: string }>({
  disabled,
  name,
  label,
  labelIcon,
  tooltip,
  options,
  placeholder,
  clearable,
  loading,
  required,
  getOptionLabel,
  noOptionsText,
  validate = [],
  onChange,
  onChildrenLoad,
  onSearch,
  onLoadTree,
  testId,
  renderSearchOption,
  renderOption,
  withoutMargins,
}: TreeViewAutocompleteProps<T>) => {
  const { input, meta } = useField(name, {
    validate: compose<string>(Array.isArray(validate) ? validate : [validate]),
  });

  /**
   * Обработчик изменения значения поля.
   *
   * @param value - Новое значение поля.
   * @param item - Элемент дерева.
   */
  const handleChange = useCallback(
    (value?: string, item?: Item) => {
      input.onChange(value);

      if (onChange) {
        onChange(value, item);
      }
    },
    [input, onChange],
  );

  const handleBlur = useCallback(() => {
    input.onBlur();
  }, [input]);

  return (
    <Input
      {...input}
      value={input.value}
      testId={testId}
      onChange={handleChange}
      onBlur={handleBlur}
      label={label}
      labelIcon={labelIcon}
      options={options}
      disabled={disabled}
      tooltip={tooltip}
      placeholder={placeholder}
      clearable={clearable}
      loading={loading}
      required={required}
      getOptionLabel={getOptionLabel}
      noOptionsText={noOptionsText}
      error={meta.touched && !!meta.error}
      helperText={meta.touched && meta.error}
      onChildrenLoad={onChildrenLoad}
      onSearch={onSearch}
      onLoadTree={onLoadTree}
      renderSearchOption={renderSearchOption}
      renderOption={renderOption}
      withoutMargins={withoutMargins}
    />
  );
};
